/**
 * Addons Slice
 *
 * Manages flight addons state including:
 * - Addons data
 * - Meals data
 * - Baggage data
 * - Coupons data
 */
import { createSlice } from '@reduxjs/toolkit';
import type {
  FlightsAddonsComponentData,
  FlightsAddonMealsComponentData,
  FlightsAddonBaggageComponentData,
  FlightsCouponsComponentData,
  FlightsSeatsComponentData,
  ApplyCouponData,
} from '@contracts';
import {
  fetchAddonsThunk,
  fetchMealsThunk,
  fetchBaggageThunk,
  fetchCouponsThunk,
  fetchSeatsThunk,
  selectMealThunk,
  selectBaggageThunk,
  selectAddonThunk,
  selectCouponThunk,
} from '../thunks/addonsThunks';

// ============================================
// State Types
// ============================================

interface AddonDataState<T = unknown> {
  data: T | null;
  isLoading: boolean;
  error: { message: string } | null;
}

interface CouponsState extends AddonDataState<FlightsCouponsComponentData> {
  appliedCouponData: ApplyCouponData | null;
}

export interface AddonsState {
  addons: AddonDataState<FlightsAddonsComponentData>;
  meals: AddonDataState<FlightsAddonMealsComponentData>;
  baggage: AddonDataState<FlightsAddonBaggageComponentData>;
  seats: AddonDataState<FlightsSeatsComponentData>;
  coupons: CouponsState;
}

// ============================================
// Initial State
// ============================================

const createInitialAddonDataState = <T>(): AddonDataState<T> => ({
  data: null,
  isLoading: false,
  error: null,
});

export const initialAddonsState: AddonsState = {
  addons: createInitialAddonDataState<FlightsAddonsComponentData>(),
  meals: createInitialAddonDataState<FlightsAddonMealsComponentData>(),
  baggage: createInitialAddonDataState<FlightsAddonBaggageComponentData>(),
  seats: createInitialAddonDataState<FlightsSeatsComponentData>(),
  coupons: {
    ...createInitialAddonDataState<FlightsCouponsComponentData>(),
    appliedCouponData: null,
  },
};

// ============================================
// Slice
// ============================================

const addonsSlice = createSlice({
  name: 'flights/addons',
  initialState: initialAddonsState,
  reducers: {
    resetAddonsState: () => initialAddonsState,

    clearAddons: (state) => {
      state.addons = createInitialAddonDataState<FlightsAddonsComponentData>();
    },

    clearMeals: (state) => {
      state.meals = createInitialAddonDataState<FlightsAddonMealsComponentData>();
    },

    clearBaggage: (state) => {
      state.baggage = createInitialAddonDataState<FlightsAddonBaggageComponentData>();
    },

    clearCoupons: (state) => {
      state.coupons = {
        ...createInitialAddonDataState<FlightsCouponsComponentData>(),
        appliedCouponData: null,
      };
    },

    clearSeats: (state) => {
      state.seats = createInitialAddonDataState<FlightsSeatsComponentData>();
    },
  },
  extraReducers: (builder) => {
    // Fetch Addons
    builder
      .addCase(fetchAddonsThunk.pending, (state) => {
        state.addons.isLoading = true;
        state.addons.error = null;
      })
      .addCase(fetchAddonsThunk.fulfilled, (state, action) => {
        state.addons.isLoading = false;
        state.addons.data = action.payload;
      })
      .addCase(fetchAddonsThunk.rejected, (state, action) => {
        state.addons.isLoading = false;
        state.addons.error = { message: action.payload ?? 'Failed to fetch addons' };
      });

    // Select Addon
    // Note: We don't set isLoading here to avoid showing full-page loader
    // Individual item loading is handled by selectingAddonIndex state in component
    builder
      .addCase(selectAddonThunk.fulfilled, (state, action) => {
        const { itemCode, sectorId, selectionResponse } = action.payload;

        // Extract addons data from API response (contains updated cta.text and isSelected)
        const responseAddons = selectionResponse.addons as
          | FlightsAddonsComponentData['data']['addons']
          | undefined;
        if (action.payload.selectionResponse.data.seats?.data) {
          state.seats.data = { data: { seats: action.payload.selectionResponse.data.seats?.data } };
        }
        // Update the specific addon item with data from API response
        // The API response includes the full addons structure with updated cta.text
        if (state.addons.data?.data?.addons?.tabs && responseAddons?.tabs) {
          const tabs = state.addons.data.data.addons.tabs;
          const responseTabs = responseAddons.tabs;

          // Find the matching tab in response by sectorId
          const responseTab = responseTabs.find((tab) => tab.sectorId === sectorId);
          if (responseTab?.data) {
            // Find the matching item in response
            const responseItem = responseTab.data.find((item) => item.itemCode === itemCode);
            if (responseItem) {
              // Find the matching tab in state by both sectorId AND journeyIndex
              // This ensures we update the correct tab when there are multiple tabs with same sectorId
              const stateTab = tabs.find(
                (tab) => tab.sectorId === sectorId && tab.journeyIndex === responseTab.journeyIndex,
              );
              if (stateTab?.data) {
                // Find and replace the matching addon item with the one from API response
                const itemIndex = stateTab.data.findIndex((item) => item.itemCode === itemCode);
                if (itemIndex !== -1) {
                  // Replace entire item with updated data from API (includes updated cta.text and isSelected)
                  stateTab.data[itemIndex] = responseItem;
                }
              }
            }
          }
        }

        // Update ctaList if present in response
        if (selectionResponse.ctaList && state.addons.data) {
          state.addons.data.data.ctaList =
            selectionResponse.ctaList as typeof state.addons.data.data.ctaList;
        }

        // Clear any previous errors
        state.addons.error = null;
      })
      .addCase(selectAddonThunk.rejected, (state, action) => {
        // Only set error, don't set isLoading to avoid full-page loader
        state.addons.error = { message: action.payload ?? 'Failed to select addon' };
      });

    // Fetch Meals
    builder
      .addCase(fetchMealsThunk.pending, (state) => {
        state.meals.isLoading = true;
        state.meals.error = null;
      })
      .addCase(fetchMealsThunk.fulfilled, (state, action) => {
        state.meals.isLoading = false;
        state.meals.data = action.payload;
      })
      .addCase(fetchMealsThunk.rejected, (state, action) => {
        state.meals.isLoading = false;
        state.meals.error = { message: action.payload ?? 'Failed to fetch meals' };
      });

    // Select Meal
    // Note: We don't set isLoading here to avoid showing full-page loader
    // Individual item loading is handled by selectingMealIndex state in component
    // IMPORTANT: Replace entire meals state from API response (backend is single source of truth)
    builder
      .addCase(selectMealThunk.fulfilled, (state, action) => {
        const { selectionResponse } = action.payload;

        // Extract meals data from API response (contains updated cta.text and isSelected)
        // The API response structure is: { meals: { data: { title, tabs } }, fareBreakup, ctaList }
        const responseMeals = selectionResponse.meals as
          | { data: FlightsAddonMealsComponentData['data']['meals'] }
          | undefined;

        // Replace entire meals state from API response
        // Backend is the single source of truth for selection state
        if (responseMeals?.data && state.meals.data) {
          state.meals.data.data.meals = responseMeals.data;
        }

        // Update ctaList if present in response
        if (selectionResponse.ctaList && state.meals.data) {
          state.meals.data.data.ctaList =
            selectionResponse.ctaList as typeof state.meals.data.data.ctaList;
        }

        // Clear any previous errors
        state.meals.error = null;
      })
      .addCase(selectMealThunk.rejected, (state, action) => {
        // Only set error, don't set isLoading to avoid full-page loader
        state.meals.error = { message: action.payload ?? 'Failed to select meal' };
      });

    // Fetch Baggage
    builder
      .addCase(fetchBaggageThunk.pending, (state) => {
        state.baggage.isLoading = true;
        state.baggage.error = null;
      })
      .addCase(fetchBaggageThunk.fulfilled, (state, action) => {
        state.baggage.isLoading = false;
        state.baggage.data = action.payload;
      })
      .addCase(fetchBaggageThunk.rejected, (state, action) => {
        state.baggage.isLoading = false;
        state.baggage.error = { message: action.payload ?? 'Failed to fetch baggage' };
      });

    // Select Baggage
    // Note: We don't set isLoading here to avoid showing full-page loader
    // Individual item loading is handled by selectingBaggageIndex state in component
    // IMPORTANT: Replace entire baggage state from API response (backend is single source of truth)
    builder
      .addCase(selectBaggageThunk.fulfilled, (state, action) => {
        const { selectionResponse } = action.payload;

        // Extract baggage data from API response (contains updated cta.text and isSelected)
        // The API response structure is: { baggage: { data: { title, tabs } }, fareBreakup, ctaList }
        const responseBaggage = selectionResponse.baggage as
          | { data: FlightsAddonBaggageComponentData['data']['baggage'] }
          | undefined;

        // Replace entire baggage state from API response
        // Backend is the single source of truth for selection state
        if (responseBaggage?.data && state.baggage.data) {
          state.baggage.data.data.baggage = responseBaggage.data;
        }

        // Update ctaList if present in response
        if (selectionResponse.ctaList && state.baggage.data) {
          state.baggage.data.data.ctaList =
            selectionResponse.ctaList as typeof state.baggage.data.data.ctaList;
        }

        // Clear any previous errors
        state.baggage.error = null;
      })
      .addCase(selectBaggageThunk.rejected, (state, action) => {
        // Only set error, don't set isLoading to avoid full-page loader
        state.baggage.error = { message: action.payload ?? 'Failed to select baggage' };
      });

    // Fetch Coupons
    builder
      .addCase(fetchCouponsThunk.pending, (state) => {
        state.coupons.isLoading = true;
        state.coupons.error = null;
      })
      .addCase(fetchCouponsThunk.fulfilled, (state, action) => {
        state.coupons.isLoading = false;
        state.coupons.data = action.payload;
      })
      .addCase(fetchCouponsThunk.rejected, (state, action) => {
        state.coupons.isLoading = false;
        state.coupons.error = { message: action.payload ?? 'Failed to fetch coupons' };
      });

    // Fetch Seats
    builder
      .addCase(fetchSeatsThunk.pending, (state) => {
        state.seats.isLoading = true;
        state.seats.error = null;
      })
      .addCase(fetchSeatsThunk.fulfilled, (state, action) => {
        state.seats.isLoading = false;
        state.seats.data = action.payload;
      })
      .addCase(fetchSeatsThunk.rejected, (state, action) => {
        state.seats.isLoading = false;
        state.seats.error = { message: action.payload ?? 'Failed to fetch seats' };
      });

    // Select Addon - Update seats if present in response
    // builder.addCase(selectAddonThunk.fulfilled, (state, action) => {
    //   if (action.payload.selectionResponse.seats) {
    //     state.seats.data = { data: { seats: action.payload.selectionResponse.seats } };
    //   }
    // });
    // Select Coupon
    // Note: We don't set isLoading here to avoid showing full-page loader
    // Individual item loading is handled by applyingCouponCode state in component
    builder
      .addCase(selectCouponThunk.fulfilled, (state, action) => {
        const { itemCode, selectionResponse, isSelected } = action.payload;

        // Extract coupons data from API response (contains updated isSelected)
        const responseCoupons = selectionResponse.coupons as
          | FlightsCouponsComponentData
          | undefined;

        // Update the specific coupon item's isSelected flag
        // IMPORTANT: Only ONE coupon can be selected at a time
        // When selecting a coupon, all other coupons must be deselected
        if (state.coupons.data?.coupons?.coupons && responseCoupons?.coupons?.coupons) {
          const coupons = state.coupons.data.coupons.coupons;
          const responseCouponsList = responseCoupons.coupons.coupons;

          // If selecting a coupon (not deselecting), first deselect all other coupons
          if (isSelected) {
            // Deselect all coupons first
            for (let i = 0; i < coupons.length; i++) {
              const existingCoupon = coupons[i];
              if (existingCoupon && existingCoupon.code !== itemCode) {
                coupons[i] = {
                  code: existingCoupon.code,
                  description: existingCoupon.description,
                  discount: existingCoupon.discount,
                  isSelected: false,
                  cta: existingCoupon.cta,
                };
              }
            }
          }

          // Find and update the matching coupon item
          const couponIndex = coupons.findIndex((coupon) => coupon.code === itemCode);
          const responseCoupon = responseCouponsList?.find((coupon) => coupon.code === itemCode);

          if (couponIndex !== -1 && responseCoupon) {
            // Replace entire coupon with updated data from API (includes updated isSelected)
            coupons[couponIndex] = responseCoupon;
          }
        } else if (state.coupons.data?.coupons?.coupons) {
          // Fallback: manually update isSelected if API didn't return full coupons data
          const coupons = state.coupons.data.coupons.coupons;

          // If selecting a coupon (not deselecting), first deselect all other coupons
          if (isSelected) {
            // Deselect all coupons first
            for (let i = 0; i < coupons.length; i++) {
              const existingCoupon = coupons[i];
              if (existingCoupon && existingCoupon.code !== itemCode) {
                coupons[i] = {
                  code: existingCoupon.code,
                  description: existingCoupon.description,
                  discount: existingCoupon.discount,
                  isSelected: false,
                  cta: existingCoupon.cta,
                };
              }
            }
          }

          // Update the selected coupon
          const couponIndex = coupons.findIndex((coupon) => coupon.code === itemCode);
          if (couponIndex !== -1) {
            const existingCoupon = coupons[couponIndex];
            if (existingCoupon) {
              coupons[couponIndex] = {
                code: existingCoupon.code,
                description: existingCoupon.description,
                discount: existingCoupon.discount,
                isSelected,
                cta: existingCoupon.cta,
              };
            }
          }
        }

        if (selectionResponse.data.couponData && isSelected) {
          const couponData = selectionResponse.data.couponData as {
            header?: { icon: string };
            couponCode: string;
            couponInfo?: { title: string };
            title?: string;
            message?: string;
            description?: string;
            couponPersuasion?: { title: string; icon: string };
            cta?: {
              label: string;
              actionType: string;
              buttonVariant?: string;
            };
          };

          state.coupons.appliedCouponData = {
            header: couponData.header ?? { icon: '' },
            couponCode: couponData.couponCode,
            couponInfo: couponData.couponInfo ?? { title: '' },
            title: couponData.title ?? '',
            message: couponData.message ?? '',
            description: couponData.description ?? '',
            couponPersuasion: couponData.couponPersuasion,
            cta: couponData.cta
              ? {
                  label: couponData.cta.label,
                  actionType: couponData.cta.actionType,
                  buttonVariant:
                    (couponData.cta.buttonVariant as 'solid' | 'outline' | 'text' | 'gradient') ??
                    'solid',
                }
              : { label: 'OK', actionType: 'close', buttonVariant: 'solid' as const },
          };
        } else if (!isSelected) {
          state.coupons.appliedCouponData = null;
        }

        // Clear any previous errors
        state.coupons.error = null;
      })
      .addCase(selectCouponThunk.rejected, (state, action) => {
        // Only set error, don't set isLoading to avoid full-page loader
        state.coupons.error = { message: action.payload ?? 'Failed to select coupon' };
      });
  },
});

// ============================================
// Actions
// ============================================

export const { resetAddonsState, clearAddons, clearMeals, clearBaggage, clearCoupons, clearSeats } =
  addonsSlice.actions;

// ============================================
// Reducer Export
// ============================================

export const addonsReducer = addonsSlice.reducer;
